import React from "react";
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
} from "@chakra-ui/react";
import { FaRegQuestionCircle } from "react-icons/fa";
import { ProfitAndLossItems } from "../../../contexts/constants";
import { useTranslation } from "react-i18next";

const P = "features.InvestmentScreening.Popover.rationalizationBefore";
const PL = "features.InvestmentScreening.Popover.profitLossExplanation";
const C = "common.label";

// レベルに応じたインデント（px）を取得
const getLevelIndent = (level: number): number => {
  switch (level) {
    case 1: return 0;
    case 2: return 6;
    case 3: return 12;
    case 4: return 16;
    default: return 0;
  }
};

/**
 * 年度別損益（合理化前）の項目説明ポップオーバー
 *
 * 使用例:
 * <RationalizationBeforeProfitLossExplanationPopover />
 */
export const RationalizationBeforeProfitLossExplanationPopover: React.FC<{ isGeneralPurpose?: boolean }> = ({ isGeneralPurpose }) => {
  const { t } = useTranslation();

  const qpLabel = isGeneralPurpose ? t(`${PL}.qpLabelGeneral`) : t(`${PL}.qpLabelNormal`);
  const qLabel = isGeneralPurpose ? t(`${PL}.qLabelGeneral`) : t(`${PL}.qLabelNormal`);

  const explanationData = [
    { item: ProfitAndLossItems.totalSales, description: t(`${PL}.totalOf2to5`), isBold: true },
    { item: ProfitAndLossItems.productSales, description: t(`${PL}.screenInput`, { label: qpLabel }) },
    { item: ProfitAndLossItems.moldRecovery, description: t(`${PL}.screenInput`, { label: qpLabel }) },
    { item: ProfitAndLossItems.devRecovery, description: t(`${PL}.screenInput`, { label: qpLabel }) },
    { item: ProfitAndLossItems.equipmentRecovery, description: t(`${PL}.screenInput`, { label: qpLabel }) },
    { item: ProfitAndLossItems.salesCost, description: t(`${PL}.totalOf7to18`), isBold: true },
    { item: ProfitAndLossItems.materialCost, description: t(`${PL}.materialCostFormula`, { qLabel }) },
    { item: ProfitAndLossItems.outsideCost, description: t(`${PL}.outsideCostFormula`) },
    { item: ProfitAndLossItems.laborCost, description: t(`${PL}.laborCostFormula`, { qLabel }) },
    { item: ProfitAndLossItems.domesticLaborCost, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.factoryExpenses, description: t(`${PL}.totalOf12to16`) },
    { item: ProfitAndLossItems.overHeadCost, description: t(`${PL}.overHeadCostFormula`, { qLabel }) },
    { item: ProfitAndLossItems.depreciation, description: t(`${PL}.totalOf14to16`) },
    { item: ProfitAndLossItems.equipmentDepreciation, description: t(`${PL}.equipDepreciationDesc`) },
    { item: ProfitAndLossItems.factoryCost, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.otherEquipmentCost, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.domesticFactoryExpenses, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.scrapCost, description: t(`${PL}.scrapCostFormula`, { qLabel }) },
    { item: ProfitAndLossItems.sgaExpenses, description: t(`${PL}.totalOf20to24`), isBold: true },
    { item: ProfitAndLossItems.appliedLaborCost, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.domesticSgaCost, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.salesManagementCost, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.devCost, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.royalty, description: t(`${PL}.expenseScreenInput`) },
    { item: ProfitAndLossItems.otherCost, description: t(`${PL}.expenseScreenInput`), isBold: true },
    { item: ProfitAndLossItems.operatingProfit, description: t(`${PL}.operatingProfitFormula`), isBold: true },
    { item: ProfitAndLossItems.operatingProfitRate, description: t(`${PL}.operatingProfitRateFormula`), isBold: true },
    { item: ProfitAndLossItems.realOperatingProfitRate, description: t(`${PL}.realOperatingProfitRateFormula`), isBold: true },
  ];

  return (
    <Popover placement="right">
      <PopoverTrigger>
        <button style={{ background: 'none', border: 'none', padding: 0 }}>
          <FaRegQuestionCircle size="30px" cursor="pointer" />
        </button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="1000px" maxH="700px" overflowY="auto" bgColor="#fffff0">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold">{t(`${P}.title`)}</PopoverHeader>
          <PopoverBody>
            <Box mb={4}>
              <Table size="sm" variant="simple" mb={4}>
                <Thead>
                  <Tr>
                    <Th w="200px">{t(`${C}.period`)}</Th>
                    <Th>{t(`${C}.description`)}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>{t(`${P}.beforeMassProduction`)}</Td>
                    <Td>{t(`${P}.beforeMassProdDesc`)}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t(`${P}.afterToEol`)}</Td>
                    <Td>{t(`${P}.afterToEolDesc`)}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            <Box>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th w="60px">{t(`${C}.no`)}</Th>
                    <Th w="300px">{t(`${C}.item`)}</Th>
                    <Th>{t(`${C}.description`)}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {explanationData.map((row, index) => (
                    <Tr key={index}>
                      <Td>{index + 1}</Td>
                      <Td
                        fontWeight={row.isBold ? "bold" : "normal"}
                        pl={getLevelIndent(row.item.level)}
                      >
                        {row.item.name}
                      </Td>
                      <Td>{row.description}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
